import { Badge, type BadgeProps } from '@/components/ui/badge';

const STATUS_VARIANT_MAP: Record<string, BadgeProps['variant']> = {
  generated: 'info',
  // Terminal states. From the buyer's POV the order finishes at 'received'
  // (stock landed on the shelf). From the warehouse's POV it finishes at
  // 'fulfilled' (we shipped it). Both render green so each side sees a
  // consistent "this is done" colour, even though the canonical DB status
  // continues to advance to 'received' once the buyer marks it.
  received: 'success',
  fulfilled: 'success',
  cancelled: 'destructive',
  active: 'success',
  inactive: 'default',
  paused: 'warning',
  pending: 'warning',
  processing: 'info',
  submitted: 'info',
  completed: 'success',
  failed: 'destructive',
  returned: 'orange',
  refunded: 'teal',
  'partially refunded': 'teal',
  // ACH refund submitted, waiting to settle
  'refund processing': 'info',
  'partial refund processing': 'info',
  draft: 'default',
  sent: 'info',
  partial: 'indigo',
  paid: 'success',
  overdue: 'destructive',
  void: 'default',
};

interface StatusBadgeProps {
  status: string;
  className?: string;
  /**
   * Per-context label override. Lets the same DB status render with different
   * wording in different views (e.g. 'generated' → 'Sent' in the buyer's PO
   * list, 'In Progress' in the warehouse SO list) without changing the DB enum.
   */
  labelMap?: Record<string, string>;
}

/** Multi-word statuses that read better with every word capitalized. */
const STATUS_LABELS: Record<string, string> = {
  'refund processing': 'Refund Processing',
  'partial refund processing': 'Partial Refund Processing',
  'partially refunded': 'Partially Refunded',
};

/** Capitalize first letter for UI display (database values remain unchanged) */
function formatStatus(value: string): string {
  if (!value) return value;
  return STATUS_LABELS[value.toLowerCase()] ?? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function StatusBadge({ status, className, labelMap }: StatusBadgeProps) {
  const key = status.toLowerCase();
  const variant = STATUS_VARIANT_MAP[key] || 'default';
  const label = labelMap?.[key] ?? formatStatus(status);
  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}
